// API configuration and utilities
import { sessionKeyManager } from './encryption';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

class ApiClient {
  constructor() {
    this.baseURL = API_URL;
  }

  getToken() {
    return localStorage.getItem('selah_token');
  }

  setToken(token) {
    localStorage.setItem('selah_token', token);
  }

  clearToken() {
    localStorage.removeItem('selah_token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token && !options.skipAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        this.clearToken();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
        // Preserve structured error info (status + detail object) so callers
        // can branch on specific error codes (e.g. 409 subscription_already_active).
        const detail = error.detail;
        const message =
          typeof detail === 'string'
            ? detail
            : detail?.message ||
              detail?.code ||
              `HTTP error! status: ${response.status}`;
        const err = new Error(message);
        err.status = response.status;
        err.detail = detail;
        throw err;
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Auth methods
  async register(email, password, name) {
    const data = await this.post('/api/auth/register', { email, password, name }, { skipAuth: true });
    this.setToken(data.access_token);
    // Persist email for deterministic encryption-key derivation
    sessionKeyManager.setUserEmail(email);
    return data;
  }

  async login(email, password) {
    const data = await this.post('/api/auth/login', { email, password }, { skipAuth: true });
    this.setToken(data.access_token);
    // Persist email for deterministic encryption-key derivation
    sessionKeyManager.setUserEmail(email);
    return data;
  }

  async getMe() {
    const userData = await this.get('/api/auth/me');
    // Re-persist email in case localStorage was cleared
    if (userData && userData.email) {
      sessionKeyManager.setUserEmail(userData.email);
    }
    return userData;
  }

  logout() {
    this.clearToken();
    sessionKeyManager.clearKey();
  }


  // Settings/Profile management
  async updateMe(userData) {
    const data = await this.put('/api/auth/me', userData);
    // Update stored email if it changed
    if (userData.email) {
      sessionKeyManager.setUserEmail(userData.email);
    }
    return data;
  }

  async updateEmailPreferences(preferences) {
    // Convenience wrapper: PUT /api/auth/me with just email_preferences.
    return this.put('/api/auth/me', { email_preferences: preferences });
  }

  async sendWeeklyDigestPreview() {
    // Sends the current user their own digest immediately. Does NOT mark
    // `last_weekly_digest_at` so the real Saturday send still happens.
    return this.post('/api/auth/me/weekly-digest-preview', {});
  }

  async changePassword(passwordData) {
    return this.put('/api/auth/password', passwordData);
  }

  // ─────────────── Forgot / Reset Password ───────────────
  // These endpoints skip auth — they're used by signed-out users.

  async forgotPassword(email) {
    return this.post(
      '/api/auth/forgot-password',
      { email },
      { skipAuth: true },
    );
  }

  async validateResetToken(token) {
    return this.post(
      '/api/auth/reset-password/validate',
      { token },
      { skipAuth: true },
    );
  }

  async completePasswordReset(token, newPassword, email) {
    const data = await this.post(
      '/api/auth/reset-password/complete',
      { token, new_password: newPassword },
      { skipAuth: true },
    );
    // Auto-login after reset — persist the new JWT + re-derive encryption key
    this.setToken(data.access_token);
    if (email) sessionKeyManager.setUserEmail(email);
    return data;
  }

  async deleteMe() {
    return this.delete('/api/auth/me');
  }


  // Analysis
  async analyzeText(text, deeperAnalysis = false, downloadId = null) {
    const payload = { text, deeper_analysis: deeperAnalysis };
    if (downloadId) payload.download_id = downloadId;
    return this.post('/api/analyze', payload);
  }

  // Downloads
  async createDownload(encryptedText, encryptionIv, plaintextForAnalysis) {
    return this.post('/api/downloads', {
      encrypted_text: encryptedText,
      encryption_iv: encryptionIv,
      plaintext_for_analysis: plaintextForAnalysis,
    });
  }

  async getDownloads(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.get(`/api/downloads${params ? `?${params}` : ''}`);
  }

  async getDownload(id) {
    return this.get(`/api/downloads/${id}`);
  }

  async addFollowUp(downloadId, text, reflectionType = 'update') {
    return this.post(`/api/downloads/${downloadId}/follow-up`, {
      text,
      reflection_type: reflectionType,
    });
  }

  async deleteDownload(id) {
    return this.delete(`/api/downloads/${id}`);
  }

  // Dashboard
  async getDashboard() {
    return this.get('/api/dashboard');
  }

  // Subscription
  async getSubscriptionStatus() {
    return this.get('/api/subscription/status');
  }

  async activateSubscription(plan, platform) {
    return this.post('/api/subscription/activate', { plan, platform });
  }

  async cancelSubscription() {
    return this.post('/api/subscription/cancel', {});
  }

  // ─── Stripe subscription integration ($7.77/mo standard tier) ────
  async getStripeConfig() {
    return this.get('/api/stripe/config');
  }

  async createStripeCheckoutSession(originUrl) {
    try {
      return await this.post('/api/stripe/checkout/session', { origin_url: originUrl });
    } catch (err) {
      // Backend blocks duplicate subscriptions (HTTP 409). Instead of showing
      // a scary error, transparently take the user to the Customer Portal
      // where they can view/manage the subscription they already have.
      if (
        err?.status === 409 &&
        err?.detail?.code === 'subscription_already_active'
      ) {
        try {
          const portal = await this.createStripePortalSession(originUrl);
          if (portal?.url) {
            // Return a sentinel so the caller knows we're already navigating
            // and to skip its own redirect logic.
            return {
              url: portal.url,
              already_subscribed: true,
              message: err.detail?.message,
            };
          }
        } catch (portalErr) {
          console.error('Portal fallback failed after 409:', portalErr);
        }
        // Portal failed — re-throw with the friendly backend message.
        const friendly = new Error(
          err.detail?.message ||
            'You already have an active subscription. Please contact support if this looks wrong.'
        );
        friendly.already_subscribed = true;
        throw friendly;
      }
      throw err;
    }
  }

  async getStripeCheckoutStatus(sessionId) {
    return this.get(`/api/stripe/checkout/status/${sessionId}`);
  }

  async restoreStripePurchases(email = null) {
    return this.post('/api/stripe/restore', email ? { email } : {});
  }

  async createStripePortalSession(originUrl) {
    return this.post('/api/stripe/portal/session', { origin_url: originUrl });
  }

  // ─── Apple In-App Purchase (StoreKit 2) ─────────────────────
  //
  // These endpoints only produce meaningful results when running inside the
  // iOS wrapper (SelahIAPBridge.swift → StoreKit 2). On other platforms the
  // caller is expected to gate access via utils/platform.shouldUseAppleIAP().
  async verifyAppleTransaction({ signed_transaction, signed_renewal_info = null }) {
    return this.post('/api/apple/verify-transaction', {
      signed_transaction,
      signed_renewal_info,
    });
  }

  async restoreAppleTransactions({ signed_transactions }) {
    return this.post('/api/apple/restore', { signed_transactions });
  }

  // ─── Onboarding ────────────────────────────────────────────
  /**
   * Persist that this user has seen the "Welcome to Premium" moment.
   *
   * Called by the OnboardingSuccess modal on dismiss so the celebration
   * fires exactly once per account (survives logout, device switches,
   * and re-installs). Server returns { ok, premium_first_seen_at }.
   */
  async markPremiumOnboardingSeen() {
    return this.post('/api/user/onboarding/premium-seen', {});
  }

  // ─── Ambient Capture ────────────────────────────────────────
  async listCaptureTokens() {
    return this.get('/api/auth/capture-tokens');
  }

  async createCaptureToken(label) {
    return this.post('/api/auth/capture-tokens', { label });
  }

  async revokeCaptureToken(tokenId) {
    return this.delete(`/api/auth/capture-tokens/${tokenId}`);
  }

  async listAmbientCaptures(statusFilter = 'pending') {
    const params = new URLSearchParams({ status_filter: statusFilter }).toString();
    return this.get(`/api/ambient/captures?${params}`);
  }

  async submitInAppCapture(text, deviceLabel = 'app') {
    // POST an ambient capture using the current JWT (no capture token needed).
    // The backend accepts either X-Selah-Capture-Token or Authorization: Bearer <jwt>.
    return this.post('/api/ambient/capture', {
      text,
      source: 'app',
      device_label: deviceLabel,
    });
  }

  async promoteAmbientCapture(captureId, encryptedText, encryptionIv) {
    return this.post(`/api/ambient/captures/${captureId}/promote`, {
      encrypted_text: encryptedText,
      encryption_iv: encryptionIv,
    });
  }

  async discardAmbientCapture(captureId) {
    return this.delete(`/api/ambient/captures/${captureId}`);
  }
}

export const api = new ApiClient();
export default api;